import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { playSuccess, playClick, playCopy } from '../utils/SoundManager';
import PromoCodeRedeem from '../components/PromoCodeRedeem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PricingPage() {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showBankTransfer, setShowBankTransfer] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const bankAccounts = [
    {
      id: 'alrajhi',
      name: 'بنك الراجحي',
      icon: '🏦',
      iban: 'SA8780000618608016092847',
      color: '#004d40'
    },
    {
      id: 'alahli',
      name: 'البنك الأهلي',
      icon: '🏛️',
      iban: 'SA5710000081100001058202',
      color: '#1a237e'
    }
  ];
  const accountHolder = 'رامي علي سعيد الغامدي';

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    playCopy();
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const plans = [
    {
      id: 'weekly',
      name: 'الباقة الأسبوعية',
      price: 19,
      period: 'أسبوع',
      icon: '⚡',
      features: [
        'طلبات تواصل غير محدودة',
        'رسائل غير محدودة',
        'رؤية من شاهد ملفك',
        'شارة عضو مميز',
        'ظهور في أعلى النتائج'
      ],
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      shadowColor: 'rgba(102, 126, 234, 0.4)',
      recommended: false
    },
    {
      id: 'monthly',
      name: 'الباقة الشهرية',
      price: 69,
      period: 'شهر',
      icon: '👑',
      features: [
        'جميع مميزات الباقة الأسبوعية',
        'أولوية في الدعم الفني',
        'إشعارات فورية',
        'توصيات ذكية يومية',
        'تحليل توافق متقدم'
      ],
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      shadowColor: 'rgba(240, 147, 251, 0.4)',
      recommended: true
    },
    {
      id: 'yearly',
      name: 'الباقة السنوية',
      price: 299,
      period: 'سنة',
      icon: '💎',
      features: [
        'جميع مميزات الباقة الشهرية',
        'مستشار زواج شخصي',
        'جلسات استشارية شهرية',
        'أفضل قيمة للجادين',
        'ضمان استرداد 30 يوم'
      ],
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      shadowColor: 'rgba(79, 172, 254, 0.4)',
      recommended: false
    }
  ];

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscription/status`, { withCredentials: true });
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.log('Not logged in or error fetching subscription');
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'var(--light-bg)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(255, 107, 157, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(192, 110, 237, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(254, 193, 99, 0.1) 0%, transparent 50%)
        `,
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div className="header" style={{ position: 'relative', zIndex: 10 }}>
        <div className="header-content">
          <h1 className="logo" onClick={() => navigate('/dashboard')} style={{cursor: 'pointer'}}>وِداد</h1>
          <div className="nav-menu">
            <button className="nav-link" onClick={() => navigate('/dashboard')}>الرئيسية</button>
            <button className="nav-link" onClick={() => navigate('/search')}>البحث</button>
            <button className="nav-link" onClick={() => navigate('/messages')}>الرسائل</button>
            <button className="nav-link" style={{background: 'var(--primary)', color: 'white', borderRadius: '20px'}}>الباقات</button>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '40px', paddingBottom: '80px', position: 'relative', zIndex: 5 }}>
        
        {/* Current Subscription Status */}
        {subscriptionStatus?.is_premium && (
          <div className="card card-shine fade-in" style={{ 
            marginBottom: '24px', 
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: 'white',
            textAlign: 'center',
            padding: '28px'
          }}>
            <div className="heartbeat" style={{ fontSize: '50px', marginBottom: '12px' }}>👑</div>
            <h3 style={{ fontSize: '26px', marginBottom: '12px', color: 'white' }}>أنت عضو مميز!</h3>
            <p style={{ fontSize: '16px', opacity: 0.9, color: 'white' }}>
              تنتهي عضويتك في: {new Date(subscriptionStatus.expires_at).toLocaleDateString('ar')}
            </p>
          </div>
        )}

        {/* Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }} className="fade-in-up">
          <div className="floating" style={{ fontSize: '60px', marginBottom: '16px' }}>💎</div>
          <h1 style={{ 
            fontSize: '36px', 
            color: 'var(--text-dark)', 
            marginBottom: '16px', 
            fontWeight: '700'
          }}>
            اختر باقتك المثالية
          </h1>
          <p style={{ 
            fontSize: '18px', 
            color: 'var(--text-light)', 
            maxWidth: '600px', 
            margin: '0 auto',
            lineHeight: '1.8'
          }}>
            انضم إلى آلاف الأعضاء الذين وجدوا شريك حياتهم
          </p>
          <p style={{
            fontSize: '13px',
            color: 'var(--text-light)',
            maxWidth: '600px',
            margin: '12px auto 0',
            opacity: 0.75
          }}>
            جميع الأسعار بالريال السعودي · التجديد تلقائي ويمكن إلغاؤه في أي وقت · ضمان استرداد خلال 30 يوماً
          </p>
        </div>

        {/* Promo Code Redeem - Coupons */}
        <PromoCodeRedeem onSuccess={() => window.location.reload()} />

        {/* Plans Grid */}
        <div className="grid" style={{ 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '24px', 
          maxWidth: '1100px', 
          margin: '0 auto' 
        }}>
          {plans.map((plan) => (
            <div 
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`card stagger-item ${plan.recommended ? 'card-shine' : ''}`}
              onMouseEnter={() => setHoveredPlan(plan.id)}
              onMouseLeave={() => setHoveredPlan(null)}
              style={{ 
                position: 'relative',
                background: 'var(--card-bg)',
                border: plan.recommended 
                  ? '2px solid var(--primary)' 
                  : '1px solid rgba(0,0,0,0.1)',
                borderRadius: '20px',
                padding: '28px',
                transform: plan.recommended 
                  ? 'scale(1.02)' 
                  : hoveredPlan === plan.id ? 'translateY(-8px)' : 'scale(1)',
                transition: 'all 0.3s ease',
                boxShadow: hoveredPlan === plan.id 
                  ? `0 20px 40px ${plan.shadowColor}` 
                  : '0 4px 20px rgba(0,0,0,0.08)'
              }}
            >
              {plan.recommended && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  right: '50%',
                  transform: 'translateX(50%)',
                  background: plan.gradient,
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  boxShadow: `0 4px 15px ${plan.shadowColor}`
                }}>
                  الأكثر شعبية
                </div>
              )}

              <div style={{ 
                textAlign: 'center', 
                marginBottom: '16px',
                marginTop: plan.recommended ? '12px' : '0'
              }}>
                <div 
                  className={hoveredPlan === plan.id ? 'heartbeat' : ''}
                  style={{ fontSize: '50px', marginBottom: '12px' }}
                >
                  {plan.icon}
                </div>
                <h3 style={{ fontSize: '20px', color: 'var(--text-dark)', marginBottom: '8px', fontWeight: '700' }}>
                  {plan.name}
                </h3>
              </div>

              {/* السعر */}
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '6px' }}>
                  <span style={{ 
                    fontSize: '48px', 
                    fontWeight: '800', 
                    background: plan.gradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    {plan.price}
                  </span>
                  <span style={{ fontSize: '16px', color: 'var(--text-light)' }}>
                    ريال / {plan.period}
                  </span>
                </div>
              </div>

              <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, var(--text-light), transparent)', margin: '20px 0', opacity: 0.2 }} />

              {/* المميزات */}
              <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px' }}>
                {plan.features.map((feature, idx) => (
                  <li 
                    key={idx} 
                    style={{ 
                      padding: '10px 0', 
                      fontSize: '14px',
                      color: 'var(--text-dark)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      borderBottom: idx < plan.features.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none'
                    }}
                  >
                    <span style={{ 
                      background: plan.gradient,
                      width: '22px', height: '22px',
                      borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', color: 'white', flexShrink: 0
                    }}>✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* زر التحويل البنكي */}
              {!subscriptionStatus?.is_premium ? (
                <button 
                  data-testid={`bank-transfer-btn-${plan.id}`}
                  className="btn btn-glow ripple"
                  style={{ 
                    width: '100%',
                    background: plan.gradient,
                    color: 'white',
                    fontSize: '16px',
                    padding: '14px',
                    borderRadius: '14px',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    playClick();
                    setSelectedPlan(plan);
                    setShowBankTransfer(true);
                  }}
                >
                  🏦 اشترك الآن
                </button>
              ) : (
                <button 
                  className="btn"
                  disabled
                  style={{ 
                    width: '100%',
                    background: 'var(--text-light)',
                    color: 'white',
                    fontSize: '16px',
                    padding: '14px',
                    borderRadius: '14px',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'not-allowed'
                  }}
                >
                  أنت مشترك ✓
                </button>
              )}
            </div>
          ))}
        </div>

        {/* معلومات العضوية المجانية */}
        <div className="card" style={{ 
          textAlign: 'center', 
          marginTop: '40px',
          maxWidth: '700px',
          margin: '40px auto 0',
          padding: '28px',
          borderRadius: '16px'
        }}>
          <p style={{ fontSize: '18px', color: 'var(--text-dark)', marginBottom: '16px', fontWeight: '700' }}>
            العضوية المجانية تشمل:
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '28px', marginBottom: '4px' }}>♾️</p>
              <p style={{ fontSize: '14px', color: 'var(--text-dark)', fontWeight: '600' }}>طلبات تواصل غير محدودة</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '28px', marginBottom: '4px' }}>💬</p>
              <p style={{ fontSize: '14px', color: 'var(--text-dark)', fontWeight: '600' }}>رسالة واحدة مجانية لكل محادثة</p>
            </div>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-light)', marginTop: '16px' }}>
            يمكن إلغاء الاشتراك في أي وقت بدون أي رسوم إضافية
          </p>
        </div>

        {/* طرق الدفع */}
        <div className="card fade-in-up" style={{ 
          marginTop: '30px', 
          maxWidth: '700px', 
          margin: '30px auto 0',
          borderRadius: '20px',
          padding: '28px',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '20px', marginBottom: '16px', color: 'var(--text-dark)' }}>
            🏦 طريقة الدفع
          </h3>
          <p style={{ fontSize: '15px', color: 'var(--text-light)', lineHeight: '1.8' }}>
            التحويل البنكي عبر بنك الراجحي أو البنك الأهلي
            <br/>
            يتم تفعيل الاشتراك خلال ساعات من استلام الإيصال
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '12px', opacity: 0.7 }}>
            🔒 جميع المعاملات آمنة 100%
          </p>
        </div>

        {/* Footer Links */}
        <div style={{ 
          textAlign: 'center', 
          padding: '30px 20px 0',
          marginTop: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <span onClick={() => navigate('/contact')} style={{ cursor: 'pointer', color: 'var(--text-light)', fontSize: '13px' }}>اتصل بنا</span>
            <span onClick={() => navigate('/refund-policy')} style={{ cursor: 'pointer', color: 'var(--text-light)', fontSize: '13px' }}>سياسة الاسترداد</span>
            <span onClick={() => navigate('/terms')} style={{ cursor: 'pointer', color: 'var(--text-light)', fontSize: '13px' }}>الشروط والأحكام</span>
          </div>
        </div>
      </div>

      {/* Bank Transfer Modal */}
      {showBankTransfer && selectedPlan && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="card zoom-in" style={{ 
            maxWidth: '500px', 
            width: '100%', 
            maxHeight: '90vh', 
            overflowY: 'auto',
            borderRadius: '20px',
            padding: '28px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: 'var(--text-dark)', margin: 0, fontSize: '20px' }}>🏦 التحويل البنكي</h2>
              <button 
                data-testid="close-bank-transfer-modal"
                onClick={() => setShowBankTransfer(false)}
                className="icon-bounce"
                style={{ 
                  background: 'var(--light-bg)', 
                  border: 'none', 
                  fontSize: '18px', 
                  cursor: 'pointer',
                  color: 'var(--text-dark)',
                  width: '36px', height: '36px',
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ 
              background: selectedPlan.gradient,
              padding: '20px', 
              borderRadius: '16px', 
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '40px', marginBottom: '8px' }}>{selectedPlan.icon}</div>
              <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px', color: 'white' }}>{selectedPlan.name}</p>
              <p style={{ fontSize: '32px', fontWeight: '800', color: 'white' }}>
                {selectedPlan.price} ريال
              </p>
            </div>

            <div style={{ 
              background: 'linear-gradient(135deg, rgba(0, 150, 136, 0.1), rgba(0, 77, 64, 0.1))',
              border: '1px solid rgba(0, 150, 136, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: '#00796b', marginBottom: '12px', fontSize: '15px' }}>خطوات التحويل:</h4>
              <ol style={{ margin: 0, paddingRight: '20px', color: 'var(--text-dark)', fontSize: '14px', lineHeight: '1.8' }}>
                <li>حوّل المبلغ (<strong>{selectedPlan.price} ريال</strong>) لأحد الحسابات أدناه</li>
                <li>أرسل صورة إيصال التحويل عبر واتساب</li>
                <li>سيتم تفعيل اشتراكك خلال ساعات</li>
              </ol>
            </div>

            <div style={{ 
              background: 'var(--light-bg)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              <p style={{ color: 'var(--text-light)', fontSize: '13px', marginBottom: '6px' }}>اسم صاحب الحساب</p>
              <p style={{ color: 'var(--text-dark)', fontSize: '18px', fontWeight: '700', margin: 0 }}>
                {accountHolder}
              </p>
            </div>

            {bankAccounts.map((bank) => (
              <div 
                key={bank.id}
                style={{ 
                  background: 'var(--card-bg)',
                  border: '2px solid var(--border-color)',
                  borderRadius: '16px',
                  padding: '20px',
                  marginBottom: '16px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <span style={{ fontSize: '32px' }}>{bank.icon}</span>
                  <p style={{ fontWeight: '700', color: 'var(--text-dark)', margin: 0, fontSize: '16px' }}>{bank.name}</p>
                </div>
                
                <div style={{ 
                  background: 'var(--light-bg)',
                  borderRadius: '10px',
                  padding: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px'
                }}>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <p style={{ color: 'var(--text-light)', fontSize: '11px', marginBottom: '4px' }}>رقم الآيبان (IBAN)</p>
                    <p style={{ 
                      color: 'var(--text-dark)', 
                      fontSize: '14px', 
                      fontWeight: '600',
                      fontFamily: 'monospace',
                      margin: 0,
                      wordBreak: 'break-all'
                    }} dir="ltr">
                      {bank.iban}
                    </p>
                  </div>
                  <button
                    data-testid={`copy-iban-${bank.id}`}
                    onClick={() => copyToClipboard(bank.iban, bank.id)}
                    style={{
                      background: copiedField === bank.id 
                        ? 'linear-gradient(135deg, #00D9A5, #00B388)' 
                        : 'linear-gradient(135deg, #667eea, #764ba2)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 16px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {copiedField === bank.id ? '✓ تم النسخ' : 'نسخ'}
                  </button>
                </div>
              </div>
            ))}

            <a
              data-testid="whatsapp-receipt-btn"
              href="https://wa.me/966"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: '100%',
                background: 'linear-gradient(135deg, #25D366, #128C7E)',
                color: 'white',
                padding: '16px',
                borderRadius: '12px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                textDecoration: 'none',
                marginTop: '8px'
              }}
              onClick={() => playClick()}
            >
              أرسل إيصال التحويل عبر واتساب
            </a>

            <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '12px', color: 'var(--text-light)' }}>
              يتم تفعيل الاشتراك خلال 24 ساعة كحد أقصى
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default PricingPage;
